/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Paper, TableContainer, TableSortLabel, Button, Tabs, Tab, IconButton, Box, Snackbar } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import TextField from "@material-ui/core/TextField";
import EditIcon from "@material-ui/icons/Edit";
import useFetch from "../../utils/useFetch";
import { useHistory } from "react-router-dom";
import EditDVResponsibleModal from "./EditDVResponsibleModal";

/**
 * List of the last three months names, including the current.
 * Example: ['January', 'February', 'March']
 * Note that the current month is the current UTC month.
 */
const lastThreeMonths = (() => {
  const months = [];

  const date = new Date();
  for (let i = 2; i >= 0; i--) {
    const monthIndex = (date.getUTCMonth() - i + 12) % 12;
    const pastDate = new Date(date.getFullYear(), monthIndex);
    months.push(pastDate.toLocaleString("default", { month: "long" }));
  }

  return months;
})();

type CustomTableColumnType = {
  name: string;
  formatText?: (data: any) => string;
  createStyle?: (data: any) => object;
  key?: string;
  align: "left" | "right" | "inherit" | "center" | "justify";
  shouldFilter?: boolean;
  isButton?: boolean;
  onClick?: any;
  buttonText?: string;
};

function getMonthName(dateString) {
  if (!dateString) return "";

  const dateParts = dateString.split("/");
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JavaScript
  const day = parseInt(dateParts[2], 10);

  const date = new Date(year, month, day);
  return date.toLocaleString("default", { month: "long" });
}

const DataValidity = (props) => {
  const history = useHistory();

  const [tab, setValue] = React.useState(0);

  const [search, setSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState({
    key: "dv_responsible",
    value: "",
  });

  const [responsibleModalOpen, setResponsibleModalOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [responsibleUpdates, setResponsibleUpdates] = useState<Record<number, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, isError: false });
  const [dvDoneRefresh, setDVDoneRefresh] = useState<number>(0);

  // Search by DV responsible
  useEffect(() => {
    setSearchFilter({ key: "dv_responsible", value: search.trim() });
  }, [search]);

  const handleResponsiblePenEditClick = (row: RowType) => {
    setSelectedOutlet({
      outletId: row.outlet_id,
      outletName: row.name,
      currentResponsible: row.dv_responsible,
    });
    setResponsibleModalOpen(true);
  };

  const handleResponsibleModalClose = () => {
    setResponsibleModalOpen(false);
    setSelectedOutlet(null);
    setDVDoneRefresh(dvDoneRefresh + 1);
  };

  const handleResponsibleUpdate = (outletId: number, newResponsibleUsername: string) => {
    setResponsibleUpdates((prev) => ({
      ...prev,
      [outletId]: newResponsibleUsername,
    }));
  };

  const handleResponsibleUpdateSuccess = (isError = false) => {
    setSnackbar({ open: true, isError });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, isError: false });
  };

  // Formatting and styling functions
  const formatDate = (date) => {
    if (date) return date.split("/")[2] + "." + date.split("/")[1] + "." + date.split("/")[0];
    return "NA";
  };
  const formatDvImages = ({ approved, total }) => `${approved}/${total}`;

  // Column definitions
  const columns: Record<string, Array<CustomTableColumnType>> = {
    customers: [
      {
        name: "Property Name",
        key: "property_name",
        align: "left",
        shouldFilter: true,
      },
      {
        name: "Outlet Name",
        key: "name",
        align: "left",
        shouldFilter: true,
      },
      {
        name: "DV Date",
        key: "dv_date",
        align: "right",
        shouldFilter: true,
        formatText: formatDate,
      },
      {
        name: "Max DV Date",
        key: "max_dv_date",
        align: "right",
        shouldFilter: true,
        formatText: formatDate,
      },
      {
        name: "DV Delay",
        key: "delay",
        align: "right",
        shouldFilter: true,
      },
      {
        name: "DV status",
        key: "dv_images",
        align: "left",
        formatText: formatDvImages,
        createStyle: ({ dv_done }) => ({ color: dv_done ? "green" : "red" }),
      },
      {
        name: "Outliers",
        key: "outliers",
        align: "left",
        formatText: formatDvImages,
        createStyle: ({ approved, total }) => ({ color: approved >= total ? "green" : "red" }),
      },
      {
        name: "DV responsible",
        key: "dv_responsible",
        align: "left",
        shouldFilter: true,
      },
      {
        name: "Oldest incomplete month",
        key: "oldest_incomplete",
        align: "left",
        formatText: getMonthName,
      },
      {
        name: "Start DV",
        align: "left",
        isButton: true,
        onClick: (row: RowType) => () => history.push(`/dataValidity/${row.outlet_id}`),
        buttonText: "Go to DV",
      },
    ],
    kitrons: [
      {
        name: "User",
        key: "name",
        align: "left",
        shouldFilter: true,
      },
      {
        name: `DV tasks ${lastThreeMonths[0]}`,
        key: "dv_0",
        align: "right",
        shouldFilter: true,
      },
      {
        name: `DV tasks ${lastThreeMonths[1]}`,
        key: "dv_1",
        align: "right",
        shouldFilter: true,
      },
      {
        name: `DV tasks ${lastThreeMonths[2]}`,
        key: "dv_2",
        align: "right",
        shouldFilter: true,
      },
      {
        name: "# Assigned Outlets",
        key: "assigned",
        align: "right",
        shouldFilter: true,
      },
    ],
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Render
  return (
    <>
      <Paper
        style={{
          position: "relative",
          overflow: "scroll",
        }}
      >
        <Tabs
          textColor="primary"
          indicatorColor="primary"
          centered
          value={tab}
          onChange={(_, newTab) => setValue(newTab)}
        >
          <Tab label="Data Validity" />
          <Tab label="DV Tasks per agent" />
        </Tabs>

        {tab === 0 && (
          <TextField
            style={{
              position: "absolute",
              top: "5px",
              right: "5px",
            }}
            label="DV Responsible 🔎︎"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearch}
          />
        )}

        <CustomTable
          dataUrl={"/get-dv-status"}
          columns={columns.customers}
          filter={searchFilter}
          defaultSort={"delay"}
          defaultOrder={"desc"}
          colorDVDate={true}
          show={tab === 0}
          onEditClick={handleResponsiblePenEditClick}
          responsibleUpdates={responsibleUpdates}
        />

        <CustomTable dataUrl={`/get-dv-done?refresh=${dvDoneRefresh}`} columns={columns.kitrons} show={tab === 1} />
      </Paper>

      {selectedOutlet && (
        <EditDVResponsibleModal
          open={responsibleModalOpen}
          onClose={handleResponsibleModalClose}
          outletName={selectedOutlet.outletName}
          outletId={selectedOutlet.outletId}
          currentResponsible={selectedOutlet.currentResponsible}
          onSave={handleResponsibleUpdate}
          onSuccess={handleResponsibleUpdateSuccess}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={handleSnackbarClose}
        message={snackbar.isError ? "ERROR: Failed to update DV responsible" : "DV responsible updated successfully"}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{ style: { backgroundColor: snackbar.isError ? "#f44336" : "#4caf50" } }}
      />
    </>
  );
};

type OrderType = {
  key: string;
  direction: "asc" | "desc";
};

type FilterKV = {
  key: string;
  value: string;
};

type RowType = Record<string, any>;

type CustomTableParams = {
  dataUrl: string;
  columns: Array<CustomTableColumnType>;
  filter?: FilterKV;
  defaultSort?: string;
  defaultOrder?: "asc" | "desc";
  colorDVDate?: boolean;
  show: boolean;
  onEditClick?: (row: RowType) => void;
  responsibleUpdates?: Record<number, string>;
};

const CustomTable = ({
  dataUrl,
  columns,
  filter,
  defaultSort,
  defaultOrder,
  colorDVDate,
  show,
  onEditClick,
  responsibleUpdates,
}: CustomTableParams) => {
  const [order, setOrder] = useState<OrderType>({
    key: defaultSort || "name",
    direction: defaultOrder || "asc",
  });

  const fetchedData = useFetch(dataUrl);

  const sortingFunction = (key: string, direction: string) => (a: object, b: object) => {
    const compare = direction === "asc" ? 1 : -1;
    let a_ = a[key];
    let b_ = b[key];

    if (a_ === null || a_ === undefined) return 1;
    if (b_ === null || b_ === undefined) return -1;

    if (typeof a_ === "string") a_ = a_.toLowerCase();
    if (typeof b_ === "string") b_ = b_.toLowerCase();

    return a_ > b_ ? compare : -compare;
  };

  const filterFunction = (filterObject: FilterKV) => (row: RowType) => {
    if (!filterObject) return true;

    const { key, value } = filterObject;
    if (!value) return true;

    return row[key]?.toLowerCase().startsWith(value.toLowerCase());
  };

  const ordered = useMemo(() => {
    if (!fetchedData.loaded) return [];
    let result = [...fetchedData.data];

    // Update DV responsible without refreshing table by updating the data row if response is success
    if (responsibleUpdates) {
      result = result.map((row) => {
        if (row.outlet_id in responsibleUpdates) {
          return {
            ...row,
            dv_responsible: responsibleUpdates[row.outlet_id],
          };
        }
        return row;
      });
    }

    result = result.sort(sortingFunction(order.key, order.direction));
    return result.filter(filterFunction(filter));
  }, [fetchedData, order, filter, responsibleUpdates]);

  const getSortableHeaderCell = useCallback(
    (column: CustomTableColumnType, index: number) => {
      const { name, key, align, shouldFilter } = column;
      const { key: orderKey, direction } = order;
      const active = key === orderKey;
      const style = {
        backgroundColor: active ? "rgb(234,236,236)" : "#fafafa",
        transition: "background-color 250ms ease",
        padding: "0px",
      };

      if (!shouldFilter)
        return (
          <TableCell key={index} align={align}>
            {name}
          </TableCell>
        );

      return (
        <TableCell align={align} style={style} key={index}>
          <TableSortLabel
            active={active}
            direction={direction}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "16px",
            }}
            onClick={() =>
              setOrder({
                key: key,
                direction: orderKey === key ? (direction === "asc" ? "desc" : "asc") : "asc",
              })
            }
          >
            {name}
          </TableSortLabel>
        </TableCell>
      );
    },
    [order]
  );

  const getRowCell = useCallback(
    (row: RowType, index: number) => {
      const cells = columns.map((column, i) => {
        const { key, align, formatText, createStyle, isButton, onClick, buttonText } = column;
        const text = isButton ? buttonText : formatText ? formatText(row[key]) : row[key];
        const style = createStyle ? createStyle(row[key]) : {};
        let contentToBeRendered;

        if (isButton)
          contentToBeRendered = (
            <Button variant="outlined" onClick={onClick(row)}>
              {text}
            </Button>
          );
        else if (key === "dv_responsible") {
          contentToBeRendered = (
            <Box display="flex" alignItems="center">
              <IconButton
                size="small"
                onClick={() => onEditClick && onEditClick(row)}
                style={{
                  marginRight: "8px",
                  padding: "4px",
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Box component="span">{text}</Box>
            </Box>
          );
        } else contentToBeRendered = text;

        return (
          <TableCell scope="row" align={align} style={style} key={i}>
            {contentToBeRendered}
          </TableCell>
        );
      });

      const dv_date = row.dv_date ? new Date(row.dv_date) : null;
      const max_dv_date = row.max_dv_date ? new Date(row.max_dv_date) : null;
      const days_between_dv_and_max =
        dv_date && max_dv_date ? (max_dv_date.getTime() - dv_date.getTime()) / (1000 * 60 * 60 * 24) : null;

      // only color rows if DV is not finished for the outlet
      const bgColor = colorDVDate
        ? days_between_dv_and_max >= 4
          ? "#fbb"
          : days_between_dv_and_max >= 2
            ? "#fdb"
            : "#fff"
        : "#fff";
      return (
        <TableRow key={index} style={{ backgroundColor: bgColor }}>
          {cells}
        </TableRow>
      );
    },
    [colorDVDate, columns, onEditClick]
  );

  const headers = useMemo(() => columns.map(getSortableHeaderCell), [columns, getSortableHeaderCell]);
  const tableContent = useMemo(() => ordered.map(getRowCell), [ordered, getRowCell]);

  return (
    <TableContainer
      component={Paper}
      style={{
        display: show ? "block" : "none",
        maxHeight: "calc(100vh - 200px)",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>{headers}</TableRow>
        </TableHead>
        <TableBody>{tableContent}</TableBody>
      </Table>
      {tableContent.length === 0 && (
        <div className="noResults">
          {fetchedData.loaded ? "No results" : fetchedData.error ? "Error loading data" : "Loading..."}
        </div>
      )}
    </TableContainer>
  );
};

export default DataValidity;
